import { PlusIcon } from 'features/compositions/assets/icons/icons';
import styles from './AddNewRowsControl.module.css';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useInsertRowsService } from 'features/compositions/services/useInsertRowsService';
import { useContext } from 'react';
import ThemeContext from 'config/context/themeContext';

const AddNewRowsControl = ({newRowsMode, setNewRowsMode, newRowsData, setNewRowsData, scrollToBottom}) => {
  const { dark } = useContext(ThemeContext);
  const { selectedComposition, tempPrivateKey } = useCompositionsContext();
  const { insertRows, loading, error, resetServerStatus } = useInsertRowsService();

  // Helper function to initialize a field value based on type and default_value
  const initializeFieldValue = (field) => {
    const dv = field?.default_value;
    if (field.type === 'time_point' && dv === 'NOW()') {
      // Keep key in payload as null so optional time_point_ext serializes correctly.
      return null;
    }
    if (field.type === 'bool') {
      // Normalize bool default values across types
      if (dv === true || dv === 1 || dv === '1' || dv === 'true') return true;
      if (dv === false || dv === 0 || dv === '0' || dv === 'false') return false;
      return false;
    }
    return dv !== undefined && dv !== null ? dv : "";
  };

  // Helper function to create a new row with proper field initialization
  const createNewRow = () => {
    const newRow = {};
    
    // Initialize indexes
    if (selectedComposition?.structure?.indexes) {
      selectedComposition.structure.indexes.forEach(index => {
        newRow[index.name] = initializeFieldValue(index);
      });
    }
    
    // Initialize fields
    if (selectedComposition?.structure?.fields) {
      selectedComposition.structure.fields.forEach(field => {
        newRow[field.name] = initializeFieldValue(field);
      });
    }
    
    return newRow;
  };

  // Helper function to validate if a field is filled
  const isFieldFilled = (field, row) => {
    // Skip auto managed fields from validation (AI, AC, AU)
    if (field.ai || field.is_auto_created || field.is_auto_updated) return true;
    // Skip bool fields from validation - they have default values
    if (field.type === 'bool') return true;
    if (field.type === 'time_point' && field?.default_value === 'NOW()') return true;
    
    return row[field.name] && row[field.name].toString().trim() !== '';
  };

  const handleAddNextRow = () => {
    resetServerStatus();
    const newRow = createNewRow();
    setNewRowsData(prev => [...prev, newRow]);
    // Call scrollToBottom after the state is set
    setTimeout(scrollToBottom, 0);
  };

  const handleDecline = () => {
    setNewRowsMode(false);
    setNewRowsData([]);
    resetServerStatus();
  };

  const handleSaveNewRows = async () => {
    try {
      const success = await insertRows({
        composition: selectedComposition?.name,
        rowsData: newRowsData,
        permission: selectedComposition?.permission,
        private_key: tempPrivateKey
      });
      
      if (success) {
        setNewRowsMode(false);
        setNewRowsData([]);
      }
    } catch (error) {
      console.error('Error saving new rows:', error);
    }
  };

  const handleEnterNewRowsMode = () => {
    setNewRowsMode(true);
    resetServerStatus();
    
    const firstRow = createNewRow();
    setNewRowsData([firstRow]);
    setTimeout(scrollToBottom, 0);
  };

  // Check if all fields in all rows are filled
  const areAllFieldsFilled = () => {
    if (newRowsData.length === 0) return false;
    
    return newRowsData.every(row => {
      // Validate indexes
      if (selectedComposition?.structure?.indexes) {
        for (const index of selectedComposition.structure.indexes) {
          if (!isFieldFilled(index, row)) return false;
        }
      }
      
      // Validate fields
      if (selectedComposition?.structure?.fields) {
        for (const field of selectedComposition.structure.fields) {
          if (!isFieldFilled(field, row)) return false;
        }
      }
      
      return true;
    });
  };

  return (
    <div className={`${styles.add_new_rows_control} ${dark ? styles.dark : ''}`}>
      {newRowsMode ? (
        <button onClick={handleAddNextRow} className={styles.add_next_row_btn}>
          Add next row <PlusIcon/>
        </button>
      ) : (
        <button onClick={handleEnterNewRowsMode} className={styles.add_next_row_btn}>
          Add new row <PlusIcon/>
        </button>
      )}
      
      {newRowsMode && (
        <div className={styles.new_row_actions}>
          <button onClick={handleDecline} disabled={loading}>Decline</button>
          <button 
            onClick={handleSaveNewRows}
            disabled={!areAllFieldsFilled() || loading}
            className={!areAllFieldsFilled() || loading ? styles.disabled : ''}
          >
            {loading ? 'Saving...' : 'Save new row'}
          </button>
        </div>
      )}
      
      {error && (
        <div className={styles.error_message}>
          Error: {error}
        </div>
      )}
    </div>
  )
}

export default AddNewRowsControl;